import { UIIcon } from '../config/icons';
import { ABFilterBar } from '../lib/ab/filter-bar';
import { ABObjectCount } from '../lib/ab/widgets-admin';
import { CEventTag, CEventTagFlag, cEventTagName, c_event_tags } from '../lib/mdl/c-event-tag';
import { uiSetBG } from '../lib/ui/bg';
import { UIButton, makeActionButton } from '../lib/ui/component/button';
import { UII18NInput } from '../lib/ui/component/i18n-input';
import { UIImageInput } from '../lib/ui/component/image';
import { UITextInput } from '../lib/ui/component/input';
import { UILabeled } from '../lib/ui/component/labeled';
import { UISelect, UIMultiSelect } from '../lib/ui/component/select';
import { UITableDefinition, UITable } from '../lib/ui/component/table';
import { uiToast } from '../lib/ui/component/toast';
import { UICheckbox, UIMultiToggles } from '../lib/ui/component/toggle';
import { addTooltip } from '../lib/ui/component/tooltip';
import { showWarning } from '../lib/ui/component/warning';
import { $el } from '../lib/ui/elements';
import { $fmt } from '../lib/ui/fmt';
import { UIScreen } from '../lib/ui/screen';
import { UIValue, UIRelPos } from '../lib/ui/types';
import { UIViewSpec } from '../lib/ui/view';
import { YArrayDataSource } from '../lib/y/data-source';
import { xavis_experiments } from '../lib/y/debug';
import { $form } from '../lib/y/form';
import { $location } from '../lib/y/location';

class TagScreen extends UIScreen {
    private _tag?: CEventTag;

    constructor(tag?: CEventTag) {
        super();
        this.title = tag ? cEventTagName(tag) : 'New tag';
        this._tag = tag;
    }

    protected override fillContent(content: UIViewSpec): void {
        const tag = this._tag;
        const sidebar = $el.DIV('tl-sidebar');

        content.classList.add('tl-admin-page', 'm-sidebar');

        const name = new UII18NInput(new UITextInput);
        const description = new UII18NInput(new UITextInput({ lines: 4 }));
        const is_genre = new UICheckbox({ title: 'Use as genre' })
        const is_collection = new UICheckbox({ title: 'Is a collection' })
        const can_select = new UICheckbox({ title: 'Show in profile' })
        const slug = new UITextInput;
        const dynamic_values = [{ key: '', name: 'No' }, { key: 'weekend', name: 'This weekend' }, { key: 'stockholm', name: 'Stockholm' },
        { key: 'goteborg', name: 'Göteborg' }, { key: 'other_cities', name: 'Other cities' }]
        const dynamic = new UISelect(dynamic_values);
        const sort_values = [{ key: 'popular', name: 'Popular' }, { key: 'latest', name: 'Latest' }, { key: 'date', name: 'Date' }]
        const sort_order = new UISelect(sort_values);
        const show_cities = new UICheckbox({ title: 'Show city picker' });
        const show_past = new UICheckbox({ title: 'Show past events' });
        const show_future = new UICheckbox({ title: 'Show events beyond 7 weeks' });
        const show_all_cities = new UICheckbox({ title: 'Show events in all cities' });
        const og_title = new UITextInput;
        const og_description = new UITextInput({ lines: 3 });
        const og_image = new UIImageInput({ scope: 'event_tag' });
        const is_hidden = new UICheckbox({ title: 'Hide in sitemap' });
        const event_creation_flags_values: UIValue<CEventTagFlag>[] = [{ key: 'create_no_length', name: 'No length' }, { key: 'create_dropin', name: 'Drop-in sessions' }]
        const event_creation_flags = new UIMultiSelect(event_creation_flags_values, {});

        const country_values = [
            { key: 'SE', title: 'Sweden' },
            { key: 'DE', title: 'Germany' }
        ];
        const carousel = new UIMultiToggles(country_values, v => new UICheckbox(v), { classes: 'l-form' });
        const carousel_image = new UIImageInput({ scope: 'event_tag' });

        const form = $form({ _: [show_cities, show_past, show_future, show_all_cities, event_creation_flags], name, description, is_genre, is_collection, can_select, slug, dynamic, sort_order, is_hidden, og_image, og_title, og_description, carousel, carousel_image });

        if(tag) {
            form.value = tag;
            show_cities.value = tag.flags.show_cities || false;
            show_past.value = tag.flags.show_past || false;
            show_future.value = tag.flags.show_future || false;
            show_all_cities.value = tag.flags.show_all_cities || false;

            const eflags: CEventTagFlag[] = [];
            if(tag.flags.create_no_length) eflags.push('create_no_length');
            if(tag.flags.create_dropin) eflags.push('create_dropin');
            event_creation_flags.value = eflags;
        } else {
            sort_order.value = 'popular';
        }

        const section = $el.FORM('ui-section w-form l-form', [
            new UILabeled(name, { above: 'Name' }),
            new UILabeled(description, { above: 'Description' }),
            is_genre,
            is_collection,
            can_select,
            new UILabeled(slug, { above: 'Slug' }),
            new UILabeled(dynamic, { above: 'Dynamic' }),
            new UILabeled(sort_order, { above: 'Sort order' }),
            show_cities,
            show_past,
            show_future,
            show_all_cities,
            $el.H3('heading', 'Share metadata'),
            $el.P('input-hint', 'Will be used instead of the default one if provided'),
            new UILabeled(og_title, { above: 'og:title' }),
            new UILabeled(og_description, { above: 'og:description' }),
            $el.DIV('l-input-row tl-pictures-row', [
                new UILabeled(og_image, { above: 'og:image', below: 'Recommended: 1200x630 ~100kb', classes: 'r-desktop' })
            ]),
            is_hidden,
            xavis_experiments && new UILabeled(event_creation_flags, { above: 'Event creation flags' }),
            new UILabeled(carousel, { above: 'Show as carousel in' }),
            $el.DIV('l-input-row tl-pictures-row', [
                new UILabeled(carousel_image, { above: 'Carousel image', below: 'Recommended: 800x1000 ~100kb', classes: 'r-card' })
            ])
        ]);

        const button = new UIButton({
            icon: UIIcon.THUMBS_UP, kind: 'k-square', classes: 'm-round ui-action-button', on_click: () => {
                if(!form.validate()) return;

                const value = form.value;
                value.flags = {
                    show_cities: show_cities.value ? true : undefined,
                    show_all_cities: show_all_cities.value ? true : undefined,
                    show_past: show_past.value ? true : undefined,
                    show_future: show_future.value ? true : undefined,
                    create_no_length: event_creation_flags.value.includes('create_no_length') ? true : undefined,
                    create_dropin: event_creation_flags.value.includes('create_dropin') ? true : undefined
                }

                c_event_tags.upsert(tag && tag.id, value).then($location.pop, uiToast);
            }
        });

        addTooltip(tag ? 'Update' : 'Create', button, { offset: 8, hover: button, position: UIRelPos.Above });

        content.ui_add([section, sidebar, button]);
    }
}

export class AdminTagsScreen extends UIScreen {
    protected override async fillContent(content: UIViewSpec) {
        const all_tags = await c_event_tags.getAll();

        const doSearch = () => {
            count.number = all_tags.length;
            source.data = all_tags;
        }

        const count = new ABObjectCount('tags');

        const filter_bar = new ABFilterBar({
            right_content: [count],
            no_header: true
        });

        const def = new UITableDefinition<CEventTag>();

        def.addCustom({
            width: -50, classes: 'tl-thumb-cell', decorator: (td, row) => {
                const tag = row.item;
                const image = $el.DIV('r-image');
                uiSetBG(image, { media: tag.carousel_image ?? tag.og_image });
                td.appendChild(image);
            }
        });
        def.addValue(p => cEventTagName(p), { title: 'Name', width: 150 });
        def.addValue(p => p.slug ?? '-', { title: 'Slug', width: 120 });
        def.addValue(p => p.is_genre, $fmt.CHECKMARK, { title: 'Genre', width: -70 });
        def.addValue(p => p.is_collection, $fmt.CHECKMARK, { title: 'Collection', width: -105 });
        def.addValue(p => !!p.dynamic, $fmt.CHECKMARK, { title: 'Dynamic', width: -90 });

        def.addButtons([
            {
                icon: UIIcon.TRASH_CAN, tooltip: 'Delete', action: item => {
                    return showWarning({ text: "Are you sure you want to delete this tag?" }).then(() => {
                        return c_event_tags.remove(item.id).then(() => {
                            doSearch();
                        });
                    });
                }
            },
            {
                icon: UIIcon.PENCIL, tooltip: 'Edit', action: item => {
                    $location.push({ transient: new TagScreen(item) });
                }
            }
        ], { width: -125 });

        def.preference_prefix = 'event_tags';

        const source = new YArrayDataSource<CEventTag>();

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block', [
                filter_bar,
                new UITable(def, source),
            ])),
            makeActionButton(UIIcon.PLUS, 'New tag', () => { $location.push({ transient: new TagScreen }) })
        ]);

        doSearch();
    }
}
